import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageHeader, Card, SegmentedControl, TextInput, Textarea, Button, Badge,
  ErrorState, Alert, useToast, useTheme,
} from '@parsina/framework';
import { THEME_PRESETS, FONT_SIZES } from '@parsina/framework/tokens';
import { Info } from 'lucide-react';
import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { useProfile, JOIN_CLINIC } from '../../hooks/useProfile.js';
import { bffMutation, errorCode } from '../../lib/bffClient.js';
import { sortPresences, presenceDayKey, minutesToClock } from '../../lib/presence.js';
import Panel from '../../components/common/Panel.jsx';
import { ProfileSkeletonWeb } from './Profile.skeleton.jsx';
import {
  PROFILE_SECTIONS, snapshot, changedOnly, isDirty, isMedicalCodeLocked, languageOptions,
} from './Profile.shared.js';

// پروفایل — identity, clinics, shifts, appearance.
//
// Two save models, on purpose:
//   the identity FORM commits on a button, because typing a half-finished
//     medical code is not intent;
//   the appearance PICKERS commit immediately and paint locally first, because
//     the whole point of an accent picker is seeing the accent.
//
// The specialty field is READ-ONLY and shows the dental string. This panel IS
// the dental module; changing the specialty elsewhere closes it to the account,
// and offering an editor for that here would be offering a door out of the
// building labelled "settings".

// The section set and the identity edit model live in Profile.shared.js — the
// mobile surface renders the same four sections and commits the same changed
// keys, and "what counts as dirty" existing twice is how two forms come to
// disagree about whether there is anything to save.
const SECTIONS = PROFILE_SECTIONS;

const ProfileScreen = () => {
  const { t, locale, setLocale } = useTranslation();
  const toast = useToast();
  const { setAppearance } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clinics, reload: reloadClinics } = useClinicScope();
  const { me, appearance, presences, loading, error, reload, saveProfile, saveAppearance } = useProfile();

  const section = SECTIONS(t).some((s) => s.value === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'identity';

  const setSection = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'identity') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [form, setForm] = React.useState(() => snapshot(null));
  const [saved, setSaved] = React.useState(() => snapshot(null));
  const [saving, setSaving] = React.useState(false);
  const [formError, setFormError] = React.useState(null);

  React.useEffect(() => {
    if (!me) return;
    const snap = snapshot(me);
    setForm(snap);
    setSaved(snap);
  }, [me]);

  const dirty = isDirty(form, saved);
  const codeLocked = isMedicalCodeLocked(saved);

  const submit = async () => {
    if (!form.fullName?.trim()) {
      setFormError(t('validation.fullName'));
      return;
    }
    // Unreachable while the field is locked (which is whenever `saved` holds a
    // number) — it guards the account that has none and starts typing one.
    if (saved.medicalCode && !form.medicalCode?.trim()) {
      setFormError(t('validation.medicalCode'));
      return;
    }
    const changes = changedOnly(form, saved);
    if (Object.keys(changes).length === 0) {
      toast.info(t('profile.noChanges'));
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await saveProfile(changes);
      toast.success(t('profile.saved'));
    } catch (err) {
      const msg =
        errorCode(err) === 'DUPLICATE_MEDICAL_CODE'
          ? t('profile.duplicateCode')
          : t('profile.saveFailed');
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Appearance: paint first, persist second, revert the paint on failure. ──
  const commitAppearance = async (patch) => {
    const before = { theme: appearance?.theme, fontSize: appearance?.fontSize };
    if (patch.theme || patch.fontSize) setAppearance(patch);
    try {
      await saveAppearance(patch);
      toast.success(t('profile.appearance.saved'));
    } catch {
      if (patch.theme || patch.fontSize) setAppearance(before);
      toast.error(t('profile.appearance.failed'));
    }
  };

  // Language switches the UI FIRST — nothing unmounts, so the next frame is
  // already in the new language and the confirmation toast arrives in it too.
  const commitLanguage = async (next) => {
    const previous = locale;
    setLocale(next);
    try {
      await saveAppearance({ language: next });
    } catch {
      setLocale(previous);
      toast.error(t('profile.appearance.failed'));
    }
  };

  // ── Join a clinic ────────────────────────────────────────────────────────
  const [code, setCode] = React.useState('');
  const [joining, setJoining] = React.useState(false);
  const [joinError, setJoinError] = React.useState(null);

  const join = async () => {
    const value = code.trim();
    if (!value) {
      setJoinError(t('validation.invitationCode'));
      return;
    }
    setJoining(true);
    setJoinError(null);
    try {
      const res = await bffMutation(JOIN_CLINIC, { invitationCode: value });
      const name = res?.joinClinicByCode?.clinic?.name || value;
      toast.success(t('profile.clinics.joined', { name }));
      setCode('');
      reloadClinics();
    } catch (err) {
      const msg =
        errorCode(err) === 'CLINIC_NOT_FOUND'
          ? t('profile.clinics.notFound')
          : t('profile.clinics.joinFailed');
      setJoinError(msg);
    } finally {
      setJoining(false);
    }
  };

  // The page's OWN shape while the profile loads — the real title, the
  // section strip and the body of the section the URL names — so a reload on
  // ?tab=presence draws shift rows, not identity fields (DESIGN.md §"Loading &
  // skeleton").
  if (loading && !me) {
    return (
      <ProfileSkeletonWeb
        section={section}
        label={t('common.loading')}
        title={t('profile.title')}
        sub={t('profile.subtitle')}
      />
    );
  }

  if (error && !me) {
    return (
      <div style={{ height: '100%', background: C.bg, padding: 32 }} role="alert">
        <Card>
          <ErrorState
            title={t('errors.load.profile')}
            onRetry={reload}
            retryLabel={t('common.actions.retry')}
          />
        </Card>
      </div>
    );
  }

  const sortedPresences = sortPresences(presences);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      <PageHeader title={t('profile.title')} sub={t('profile.subtitle')} />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 32px 40px' }}>
        <div style={{ marginBottom: 16 }}>
          <SegmentedControl options={SECTIONS(t)} value={section} onChange={setSection} />
        </div>

        {/* The kit's SegmentedControl declares role="tablist"/role="tab", so the
            content it switches has to declare the other half of the pattern —
            otherwise a screen reader announces "tab, selected" over a region it
            has no relationship to. `aria-label` rather than `aria-labelledby`:
            the kit generates no ids for its tabs. */}
        <div role="tabpanel" aria-label={SECTIONS(t).find((s) => s.value === section)?.label}>
        {section === 'identity' && (
          <Panel
            title={t('profile.sections.identity')}
            action={
              <Button onClick={submit} disabled={!dirty || saving}>
                {saving ? t('common.actions.saving') : t('common.actions.save')}
              </Button>
            }
          >
            {/* role="alert": the validation/save message appears after a click
                and the dentist's focus is on the ذخیره button at the top of the
                panel, nowhere near this line. */}
            {formError && (
              <Alert variant="error" role="alert" style={{ marginBottom: 14 }}>
                {formError}
              </Alert>
            )}
            {/* aria-label on every field: the kit's FormField renders its label
                as a <span> with no htmlFor/id, so the visible label is not
                attached to the control and these are unnamed text boxes. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
              {/* dir="auto", not the kit's hardcoded "rtl": a dentist may write
                  their name in either script, and a Latin name in a forced-RTL
                  box is caret-reordered as it is typed. */}
              <TextInput
                label={t('profile.fields.fullName')}
                aria-label={t('profile.fields.fullName')}
                dir="auto"
                required
                aria-required="true"
                value={form.fullName}
                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
              />
              {/* Read-only once stated — the number is this account's identity
                  in سازمان نظام پزشکی, not a profile field, and the BFF
                  drops it on save either way. See Profile.shared.js. */}
              <TextInput
                label={t('profile.fields.medicalCode')}
                aria-label={t('profile.fields.medicalCode')}
                dir="ltr"
                value={form.medicalCode}
                onChange={codeLocked
                  ? () => {}
                  : (e) => setForm((f) => ({ ...f, medicalCode: e.target.value }))}
                disabled={codeLocked}
                helperText={codeLocked ? t('profile.medicalCodeLocked') : undefined}
                data-testid="profile-medicalcode"
              />
              {/* The STORED value wins — a real row may hold
                  'متخصص دندانپزشکی', and showing a tidied-up label instead
                  would hide what the account actually carries. The localised
                  label is only the fallback for a row with no specialty yet. */}
              <TextInput
                label={t('profile.fields.specialty')}
                aria-label={t('profile.fields.specialty')}
                dir="auto"
                value={me?.specialty || t('profile.specialtyValue')}
                onChange={() => {}}
                disabled
                helperText={t('profile.specialtyLocked')}
              />
            </div>
            <div style={{ marginTop: 14 }}>
              <Textarea
                label={t('profile.fields.bio')}
                aria-label={t('profile.fields.bio')}
                dir="auto"
                rows={3}
                value={form.bio}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
              />
            </div>
          </Panel>
        )}

        {section === 'clinics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Panel title={t('profile.sections.clinics')}>
              {clinics.length === 0 ? (
                <div style={{ fontSize: 13, color: C.textMuted }}>{t('profile.clinics.empty')}</div>
              ) : (
                clinics.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 0',
                      borderBottom: `1px solid ${C.border}`,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>
                      {m.clinic?.name || m.clinicId}
                    </span>
                    {m.clinic?.city && (
                      <span style={{ fontSize: 12, color: C.textMuted }}>{m.clinic.city}</span>
                    )}
                    {m.visitDurationMinutes ? (
                      <span style={{ fontSize: 12, color: C.textMuted }}>
                        {t('profile.clinics.visitDuration', { count: m.visitDurationMinutes })}
                      </span>
                    ) : null}
                    <Badge
                      variant={m.status === 'pending' ? 'warning' : 'success'}
                      className="ms-auto"
                    >
                      {m.status === 'pending'
                        ? t('profile.clinics.status.pending')
                        : t('profile.clinics.status.active')}
                    </Badge>
                  </div>
                ))
              )}
            </Panel>

            <Panel title={t('profile.clinics.joinTitle')}>
              <div style={{ fontSize: 12.5, color: C.textMuted, marginBottom: 12 }}>
                {t('profile.clinics.joinHint')}
              </div>
              {joinError && (
                <Alert variant="error" role="alert" style={{ marginBottom: 12 }}>
                  {joinError}
                </Alert>
              )}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                <TextInput
                  label={t('profile.clinics.joinPlaceholder')}
                  aria-label={t('profile.clinics.joinPlaceholder')}
                  dir="ltr"
                  value={code}
                  // A one-field form: Enter submits it. Without this the only way
                  // to join was to leave the keyboard for the button.
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !joining) {
                      e.preventDefault();
                      join();
                    }
                  }}
                  onChange={(e) => setCode(e.target.value)}
                  style={{ flex: 1, minWidth: 220 }}
                />
                <Button onClick={join} disabled={joining}>
                  {joining ? t('common.actions.saving') : t('common.actions.join')}
                </Button>
              </div>
            </Panel>
          </div>
        )}

        {section === 'presence' && (
          <Panel title={t('profile.sections.presence')}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
                fontSize: 12,
                color: C.textMuted,
              }}
            >
              <Info size={14} />
              <span>{t('profile.presence.readOnly')}</span>
            </div>
            {sortedPresences.length === 0 ? (
              <div style={{ fontSize: 13, color: C.textMuted }}>{t('profile.presence.empty')}</div>
            ) : (
              sortedPresences.map((p) => {
                const dayKey = presenceDayKey(p.dayOfWeek);
                const clinic = clinics.find((c) => c.clinicId === p.clinicId);
                return (
                  <div
                    key={p.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '9px 0',
                      borderBottom: `1px solid ${C.border}`,
                      opacity: p.isActive ? 1 : 0.55,
                    }}
                  >
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary, minWidth: 90 }}>
                      {dayKey ? t(dayKey) : t('profile.presence.oneOff')}
                    </span>
                    <span dir="ltr" style={{ unicodeBidi: 'isolate', fontSize: 13, color: C.textSecondary }}>
                      {minutesToClock(p.startTimeMinutes)} – {minutesToClock(p.endTimeMinutes)}
                    </span>
                    {/* A disabled shift was drawn at 55% opacity and nothing
                        else — a state carried by appearance alone, which a
                        screen reader cannot report and a glance can miss. */}
                    {!p.isActive && (
                      <Badge variant="outline">{t('profile.presence.inactive')}</Badge>
                    )}
                    {clinic && (
                      <span style={{ fontSize: 12, color: C.textMuted, marginInlineStart: 'auto' }}>
                        {clinic.clinic?.name || clinic.clinicId}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </Panel>
        )}

        {section === 'appearance' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Panel title={t('profile.appearance.theme')}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {THEME_PRESETS.map((preset) => {
                  const active = (appearance?.theme || 'default') === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => commitAppearance({ theme: preset.id })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 14px',
                        borderRadius: 10,
                        border: `1.5px solid ${active ? C.navy : C.border}`,
                        background: active ? CT.accentSoft : CT.surface,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 13,
                        color: C.textPrimary,
                      }}
                    >
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${preset.swatch}, ${preset.swatch2})`,
                        }}
                      />
                      {/* The kit's preset labels are Persian-only literals, so
                          the catalogue owns the label and the preset owns the
                          swatch. */}
                      {t(`profile.theme.${preset.id}`)}
                    </button>
                  );
                })}
              </div>
            </Panel>

            <Panel title={t('profile.appearance.fontSize')}>
              <SegmentedControl
                options={FONT_SIZES.map((f) => ({ value: f.id, label: t(`profile.fontSize.${f.id}`) }))}
                value={appearance?.fontSize || 'medium'}
                onChange={(v) => commitAppearance({ fontSize: v })}
              />
            </Panel>

            <Panel title={t('profile.appearance.language')}>
              <SegmentedControl
                options={languageOptions(t)}
                value={locale}
                onChange={commitLanguage}
              />
            </Panel>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
